import { apiFetch } from "../auth/apiClient";

export const securityApi = {
  listOnDuty: ({ signal } = {}) =>
    apiFetch("/api/security/on-duty", { method: "GET", authRequired: true, signal }),

  listGuards: ({ active, signal } = {}) => {
    const qs = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
    return apiFetch(`/api/security/guards${qs}`, { method: "GET", authRequired: true, signal });
  },

  createGuard: (body, { signal } = {}) =>
    apiFetch("/api/security/guards", { method: "POST", authRequired: true, body, signal }),

  patchGuard: (id, body, { signal } = {}) =>
    apiFetch(`/api/security/guards/${id}`, { method: "PATCH", authRequired: true, body, signal }),

  getTrack: (guardId, { from, to, signal } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return apiFetch(
      `/api/security/guards/${guardId}/track${qs ? `?${qs}` : ""}`,
      { method: "GET", authRequired: true, signal }
    );
  },

  listShifts: ({ date, signal } = {}) => {
    const qs = date ? `?date=${date}` : "";
    return apiFetch(`/api/security/shifts${qs}`, { method: "GET", authRequired: true, signal });
  },

  createShift: (body, { signal } = {}) =>
    apiFetch("/api/security/shifts", { method: "POST", authRequired: true, body, signal }),

  deleteShift: (id, { signal } = {}) =>
    apiFetch(`/api/security/shifts/${id}`, { method: "DELETE", authRequired: true, signal }),

  listCheckpoints: ({ active, signal } = {}) => {
    const qs = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
    return apiFetch(`/api/patrol/checkpoints${qs}`, { method: "GET", authRequired: true, signal });
  },

  createCheckpoint: (body, { signal } = {}) =>
    apiFetch("/api/patrol/checkpoints", { method: "POST", authRequired: true, body, signal }),

  patchCheckpoint: (id, body, { signal } = {}) =>
    apiFetch(`/api/patrol/checkpoints/${id}`, { method: "PATCH", authRequired: true, body, signal }),

  deleteCheckpoint: (id, { signal } = {}) =>
    apiFetch(`/api/patrol/checkpoints/${id}`, { method: "DELETE", authRequired: true, signal }),

  getPatrolSummary: ({ date, signal } = {}) => {
    const qs = date ? `?date=${date}` : "";
    return apiFetch(`/api/patrol/summary${qs}`, { method: "GET", authRequired: true, signal });
  },

  // ── Patrol routes (security incharge) ─────────────────────────────────────
  // Mirror of backend/app/api/patrol.py /routes section. All require_security_incharge.
  listRoutes: ({ active, signal } = {}) => {
    const qs = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
    return apiFetch(`/api/patrol/routes${qs}`, { method: "GET", authRequired: true, signal });
  },

  createRoute: (body, { signal } = {}) =>
    apiFetch("/api/patrol/routes", { method: "POST", authRequired: true, body, signal }),

  patchRoute: (id, body, { signal } = {}) =>
    apiFetch(`/api/patrol/routes/${id}`, { method: "PATCH", authRequired: true, body, signal }),

  deleteRoute: (id, { signal } = {}) =>
    apiFetch(`/api/patrol/routes/${id}`, { method: "DELETE", authRequired: true, signal }),

  listRouteCheckpoints: (routeId, { signal } = {}) =>
    apiFetch(`/api/patrol/routes/${routeId}/checkpoints`, { method: "GET", authRequired: true, signal }),

  addRouteCheckpoint: (routeId, body, { signal } = {}) =>
    apiFetch(`/api/patrol/routes/${routeId}/checkpoints`, { method: "POST", authRequired: true, body, signal }),

  removeRouteCheckpoint: (routeId, checkpointId, { signal } = {}) =>
    apiFetch(`/api/patrol/routes/${routeId}/checkpoints/${checkpointId}`, { method: "DELETE", authRequired: true, signal }),

  assignRoute: (routeId, body, { signal } = {}) =>
    apiFetch(`/api/patrol/routes/${routeId}/assign`, { method: "POST", authRequired: true, body, signal }),

  // ── Compliance (supervisor) ──────────────────────────────────────────────
  getDayCompliance: ({ date, shift, signal } = {}) => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (shift) params.set("shift", shift);
    const qs = params.toString();
    return apiFetch(`/api/compliance/day${qs ? `?${qs}` : ""}`, { method: "GET", authRequired: true, signal });
  },

  getAssignmentCompliance: (assignmentId, { signal } = {}) =>
    apiFetch(`/api/compliance/assignment/${assignmentId}`, { method: "GET", authRequired: true, signal }),

  approveReport: (reportId, { review_note = null, signal } = {}) =>
    apiFetch(`/api/compliance/report/${reportId}/approve`, { method: "POST", authRequired: true, body: { review_note }, signal }),

  rejectReport: (reportId, { review_note = null, signal } = {}) =>
    apiFetch(`/api/compliance/report/${reportId}/reject`, { method: "POST", authRequired: true, body: { review_note }, signal }),

  // ── Shift handover (supervisor) ───────────────────────────────────────────
  listHandovers: ({ date, signal } = {}) => {
    const qs = date ? `?date=${encodeURIComponent(date)}` : "";
    return apiFetch(`/api/handover${qs}`, { method: "GET", authRequired: true, signal });
  },

  createHandover: (body, { signal } = {}) =>
    apiFetch("/api/handover", { method: "POST", authRequired: true, body, signal }),

  submitHandover: (id, { signal } = {}) =>
    apiFetch(`/api/handover/${id}/submit`, { method: "POST", authRequired: true, signal }),

  acknowledgeHandover: (id, { signal } = {}) =>
    apiFetch(`/api/handover/${id}/acknowledge`, { method: "POST", authRequired: true, signal }),

  // ── Incidents (security staff) ────────────────────────────────────────────
  listIncidents: ({ date, status, signal } = {}) => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (status) params.set("status", status);
    const qs = params.toString();
    return apiFetch(`/api/incidents${qs ? `?${qs}` : ""}`, { method: "GET", authRequired: true, signal });
  },

  logIncident: (body, { signal } = {}) =>
    apiFetch("/api/incidents", { method: "POST", authRequired: true, body, signal }),

  resolveIncident: (id, { resolution_note = null, signal } = {}) =>
    apiFetch(`/api/incidents/${id}/resolve`, { method: "POST", authRequired: true, body: { resolution_note }, signal }),

  // ── Roster (supervisor) ───────────────────────────────────────────────────
  // Versioned daily roster + per-guard assignments. Backend app/api/roster.py
  // (mounted /api/roster); all management endpoints require_supervisor.
  listRostersForDate: ({ date, signal } = {}) => {
    const qs = date ? `?date=${encodeURIComponent(date)}` : "";
    return apiFetch(`/api/roster${qs}`, { method: "GET", authRequired: true, signal });
  },

  getRoster: (id, { signal } = {}) =>
    apiFetch(`/api/roster/${id}`, { method: "GET", authRequired: true, signal }),

  createRoster: (body, { signal } = {}) =>
    apiFetch("/api/roster", { method: "POST", authRequired: true, body, signal }),

  publishRoster: (id, { signal } = {}) =>
    apiFetch(`/api/roster/${id}/publish`, { method: "POST", authRequired: true, signal }),

  archiveRoster: (id, { signal } = {}) =>
    apiFetch(`/api/roster/${id}/archive`, { method: "POST", authRequired: true, signal }),

  upsertAssignment: (rosterId, body, { signal } = {}) =>
    apiFetch(`/api/roster/${rosterId}/assignments`, { method: "POST", authRequired: true, body, signal }),

  deleteAssignment: (assignmentId, { signal } = {}) =>
    apiFetch(`/api/roster/assignments/${assignmentId}`, { method: "DELETE", authRequired: true, signal }),
};
